import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { translate } from '../../i18n';
import { FREE_TRIAL_MONTHS, FREE_TRIAL_PARCELS, SUPPORT_EMAIL } from '../../config/onboarding';
import { FAQ, SUGGESTED, answerOf, fill, matchFaq } from './faq';
import '../../css/chatbot.css';

const VALUES = { email: SUPPORT_EMAIL, months: FREE_TRIAL_MONTHS, parcels: FREE_TRIAL_PARCELS };

/** Lien mailto vers le support, pré-rempli avec la question posée. */
const mailtoOf = (texts, question) => {
    const subject = encodeURIComponent(texts.mail_subject);
    const body = encodeURIComponent(fill(texts.mail_body, { question: question || '' }));
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
};

/**
 * Assistant du landing : répond aux questions fréquentes (inscription, coût, résiliation,
 * contact…) à partir de la FAQ, sans service externe. Sans réponse, il propose d'écrire au support.
 */
const ChatbotWidget = () => {
    const language = useSelector((state) => state.lang?.lang) || 'FR';
    const texts = translate('chatbot', language) || translate('chatbot', 'FR');
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState('');
    const [messages, setMessages] = useState([]);
    const listRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) inputRef.current?.focus();
    }, [open]);

    useEffect(() => {
        // La dernière question posée remonte en haut du fil : sa réponse se lit depuis le début.
        const list = listRef.current;
        const questions = list ? list.querySelectorAll('.chatbot__message--user') : [];
        const question = questions[questions.length - 1];
        if (question) list.scrollTop = question.offsetTop - 8;
    }, [messages]);

    const ask = (question, entry) => {
        const found = entry || matchFaq(question, language);
        setMessages((previous) => [
            ...previous,
            { from: 'user', text: question },
            found ? { from: 'bot', faqId: found.id } : { from: 'bot', fallback: true, question },
        ]);
    };

    const submit = (event) => {
        event.preventDefault();
        const question = draft.trim();
        if (!question) return;
        ask(question);
        setDraft('');
    };

    const renderBot = (message) => {
        if (message.fallback) {
            return (
                <>
                    <p>{texts.fallback}</p>
                    <a className="chatbot__action" href={mailtoOf(texts, message.question)}>
                        {fill(texts.write_us, VALUES)}
                    </a>
                </>
            );
        }
        const entry = FAQ.find((faq) => faq.id === message.faqId);
        return (
            <>
                {answerOf(entry, texts, VALUES).map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                ))}
                <div className="chatbot__actions">
                    {entry.actions.map((action) => (action.href ? (
                        <a key={action.href} className="chatbot__action" href={action.href}>
                            {texts.links[action.label]}
                        </a>
                    ) : (
                        <Link key={action.to} className="chatbot__action" to={action.to} onClick={() => setOpen(false)}>
                            {texts.links[action.label]}
                        </Link>
                    )))}
                    {entry.contact && (
                        <a className="chatbot__action" href={mailtoOf(texts, '')}>
                            {fill(texts.write_us, VALUES)}
                        </a>
                    )}
                </div>
            </>
        );
    };

    if (!open) {
        return (
            <button type="button" className="chatbot__launcher" onClick={() => setOpen(true)} aria-label={texts.launcher}>
                <span aria-hidden="true">💬</span>
                <span className="chatbot__launcher-text">{texts.launcher}</span>
            </button>
        );
    }

    const asked = new Set(messages.filter((m) => m.faqId).map((m) => m.faqId));
    const suggestions = SUGGESTED.filter((id) => !asked.has(id));

    return (
        <section className="chatbot" role="dialog" aria-label={texts.title}>
            <div className="chatbot__header">
                <div>
                    <strong>{texts.title}</strong>
                    <small>{texts.subtitle}</small>
                </div>
                <button type="button" className="chatbot__close" onClick={() => setOpen(false)} aria-label={texts.close}>
                    ×
                </button>
            </div>

            <div className="chatbot__messages" aria-live="polite" ref={listRef}>
                <div className="chatbot__message chatbot__message--bot">
                    <p>{texts.greeting}</p>
                </div>
                {messages.map((message, index) => (
                    // Fil de discussion en ajout seul : l'index est stable.
                    <div key={index} className={`chatbot__message chatbot__message--${message.from}`}>
                        {message.from === 'user' ? <p>{message.text}</p> : renderBot(message)}
                    </div>
                ))}
                {suggestions.length > 0 && (
                    <div className="chatbot__suggestions">
                        <small>{texts.suggestions}</small>
                        {suggestions.map((id) => (
                            <button key={id} type="button" className="chatbot__suggestion"
                                    onClick={() => ask(texts.faq[id].question, FAQ.find((faq) => faq.id === id))}>
                                {texts.faq[id].question}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <form className="chatbot__form" onSubmit={submit}>
                <input
                    ref={inputRef}
                    type="text"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder={texts.placeholder}
                    aria-label={texts.placeholder}
                    maxLength={300}
                />
                <button type="submit" disabled={!draft.trim()}>{texts.send}</button>
            </form>
            <div className="chatbot__footer">
                <a href={mailtoOf(texts, '')}>{fill(texts.still_need_help, VALUES)}</a>
            </div>
        </section>
    );
};

export default ChatbotWidget;
